// components/ProductView.jsx
import React, { useState } from 'react';
import { Heart, Share2, Star, Truck, RotateCcw, Shield, ChevronLeft, ChevronRight, Plus, Minus, Check } from 'lucide-react';

const ProductView = ({ config = {} }) => {
  // Defensive config handling (same pattern as FlashBar/Cart/Search)
  const actualConfig = 
    config?.productSettings && typeof config.productSettings === 'object' && !Array.isArray(config.productSettings)
      ? config.productSettings
      : (typeof config === 'object' && !Array.isArray(config) ? config : {});

  const defaultConfig = {
    activeDesign: 'design1', // 'design1', 'design2', 'design3'
    devMode: false,
    customStyles: {},
  };

  const finalConfig = { ...defaultConfig, ...actualConfig };
  const { activeDesign, devMode, customStyles } = finalConfig;

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedColor, setSelectedColor] = useState('Blue');
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  // Product data (in real app → fetch from API or props)
  const product = {
    name: "Premium Cotton Blend T-Shirt",
    price: 79.99,
    originalPrice: 99.99,
    rating: 4.8,
    reviews: 234,
    description: "Crafted from premium cotton blend fabric, this versatile t-shirt offers exceptional comfort and style. Perfect for everyday wear, featuring a modern fit and durable construction.",
    features: [
      "100% Premium Cotton",
      "Breathable & Comfortable",
      "Machine Washable",
      "Modern Slim Fit",
      "Reinforced Stitching"
    ],
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    colors: [
      { name: 'Blue', class: 'bg-blue-500' },
      { name: 'Black', class: 'bg-gray-900' },
      { name: 'White', class: 'bg-white border-2 border-gray-300' },
      { name: 'Green', class: 'bg-green-600' }
    ],
    images: [
      'bg-gradient-to-br from-blue-100 to-blue-200',
      'bg-gradient-to-br from-indigo-100 to-purple-200',
      'bg-gradient-to-br from-cyan-100 to-blue-200',
      'bg-gradient-to-br from-slate-100 to-gray-200'
    ],
    inStock: true
  };

  // Similar products
  const similarProducts = [
    { id: 1, name: "Classic Crew Neck Tee", price: 59.99, image: 'bg-gradient-to-br from-gray-100 to-slate-200', rating: 4.6 },
    { id: 2, name: "V-Neck Essential", price: 64.99, image: 'bg-gradient-to-br from-green-100 to-emerald-200', rating: 4.7 },
    { id: 3, name: "Henley Long Sleeve", price: 89.99, image: 'bg-gradient-to-br from-amber-100 to-orange-200', rating: 4.9 },
    { id: 4, name: "Pocket Tee Essential", price: 69.99, image: 'bg-gradient-to-br from-purple-100 to-violet-200', rating: 4.5 }
  ];

  const containerStyles = {
    background: devMode && customStyles.background ? customStyles.background : undefined,
    color: devMode && customStyles.textColor ? customStyles.textColor : undefined,
  };

  // Design 1: Classic Two-Column Layout
  const Design1 = () => (
    <div className="min-h-screen bg-gray-50" style={containerStyles}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className={`aspect-square rounded-2xl ${product.images[selectedImage]} relative overflow-hidden`}>
              <button
                onClick={() => setIsWishlisted(!isWishlisted)}
                className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-10"
              >
                <Heart className={`w-6 h-6 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-700'}`} />
              </button>

              {product.originalPrice && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-semibold text-sm">
                  Save {Math.round((1 - product.price / product.originalPrice) * 100)}%
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`aspect-square rounded-lg ${img} ${selectedImage === idx ? 'ring-2 ring-gray-900' : ''}`}
                />
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{product.name}</h1>
            
            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
                  />
                ))}
              </div>
              <span className="text-gray-600">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <span className="text-2xl text-gray-500 line-through">${product.originalPrice.toFixed(2)}</span>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-700 mb-6 leading-relaxed">{product.description}</p>

            {/* Color Selection */}
            <div className="mb-6">
              <label className="text-sm font-semibold text-gray-900 mb-3 block">
                Color: <span className="font-normal text-gray-600">{selectedColor}</span>
              </label>
              <div className="flex gap-3">
                {product.colors.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(color.name)}
                    className={`w-12 h-12 rounded-full ${color.class} ${selectedColor === color.name ? 'ring-2 ring-offset-2 ring-gray-900' : ''}`}
                  />
                ))}
              </div>
            </div>

            {/* Size Selection */}
            <div className="mb-6">
              <label className="text-sm font-semibold text-gray-900 mb-3 block">
                Size: <span className="font-normal text-gray-600">{selectedSize}</span>
              </label>
              <div className="grid grid-cols-6 gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`py-3 border-2 rounded-lg font-medium transition-colors ${selectedSize === size ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 hover:border-gray-900'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <label className="text-sm font-semibold text-gray-900 mb-3 block">Quantity</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center border-2 border-gray-300 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center hover:bg-gray-100"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="w-16 text-center font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center hover:bg-gray-100"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Add to Cart & Actions */}
            <div className="flex gap-3 mb-8">
              <button className="flex-1 bg-gray-900 text-white py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                Add to Cart
              </button>
              <button className="w-14 h-14 border-2 border-gray-900 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors">
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 border-t border-gray-200">
              <div className="flex items-center gap-3">
                <Truck className="w-6 h-6 text-gray-700" />
                <div>
                  <p className="font-semibold text-sm">Free Shipping</p>
                  <p className="text-xs text-gray-600">On orders over $50</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <RotateCcw className="w-6 h-6 text-gray-700" />
                <div>
                  <p className="font-semibold text-sm">Easy Returns</p>
                  <p className="text-xs text-gray-600">30-day guarantee</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-gray-700" />
                <div>
                  <p className="font-semibold text-sm">Secure Payment</p>
                  <p className="text-xs text-gray-600">100% protected</p>
                </div>
              </div>
            </div>

            {/* Product Features */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-900 mb-3">Key Features</h3>
              <ul className="space-y-2">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-700">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Similar Products */}
        <div className="border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {similarProducts.map((item) => (
              <div key={item.id} className="group cursor-pointer">
                <div className={`aspect-square rounded-lg ${item.image} mb-3 group-hover:scale-105 transition-transform`} />
                <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">{item.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900">${item.price}</span>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm text-gray-600">{item.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Design 2: Sticky Sidebar Layout
  const Design2 = () => (
    <div className="min-h-screen bg-white" style={containerStyles}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left: Image Gallery */}
          <div className="lg:col-span-2 space-y-4">
            <div className={`aspect-[4/5] rounded-xl ${product.images[selectedImage]}`} />
            
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`aspect-square rounded-lg ${img} ${selectedImage === idx ? 'ring-2 ring-gray-900' : 'opacity-60 hover:opacity-100'} transition-opacity`}
                />
              ))}
            </div>

            {/* Mobile Details */}
            <div className="lg:hidden space-y-6 pt-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
                <p className="text-gray-700 leading-relaxed">{product.description}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Right: Sticky Product Info */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{product.name}</h1>
              
              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-300 text-gray-300'}`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-600">({product.reviews})</span>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl font-bold text-gray-900">${product.price}</span>
                {product.originalPrice && (
                  <span className="text-lg text-gray-500 line-through">${product.originalPrice}</span>
                )}
              </div>

              {/* Color */}
              <div className="mb-6">
                <label className="text-sm font-semibold text-gray-900 mb-2 block">Color</label>
                <div className="flex gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color.name)}
                      className={`w-10 h-10 rounded-full ${color.class} ${selectedColor === color.name ? 'ring-2 ring-offset-2 ring-gray-900' : ''}`}
                    />
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="mb-6">
                <label className="text-sm font-semibold text-gray-900 mb-2 block">Size</label>
                <div className="grid grid-cols-6 gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`py-2.5 text-sm border-2 rounded-lg font-medium transition-colors ${selectedSize === size ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 hover:border-gray-900'}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="text-sm font-semibold text-gray-900 mb-2 block">Quantity</label>
                <div className="flex items-center border-2 border-gray-300 rounded-lg w-32">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center hover:bg-white"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="flex-1 text-center font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center hover:bg-white"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <button className="w-full bg-gray-900 text-white py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors mb-3">
                Add to Cart
              </button>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setIsWishlisted(!isWishlisted)}
                  className="border-2 border-gray-300 py-3 rounded-lg font-medium hover:border-gray-900 transition-colors flex items-center justify-center gap-2"
                >
                  <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
                  Wishlist
                </button>
                <button className="border-2 border-gray-300 py-4 rounded-xl font-medium hover:border-gray-900 transition-colors flex items-center justify-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>

              {/* Trust Badges */}
              <div className="space-y-3 mt-6 pt-6 border-t border-gray-300">
                <div className="flex items-center gap-3 text-sm">
                  <Truck className="w-5 h-5 text-gray-700" />
                  <span className="text-gray-700">Free shipping on orders over $50</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <RotateCcw className="w-5 h-5 text-gray-700" />
                  <span className="text-gray-700">30-day easy returns</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Shield className="w-5 h-5 text-gray-700" />
                  <span className="text-gray-700">Secure checkout guaranteed</span>
                </div>
              </div>
            </div>

            {/* Desktop Details */}
            <div className="hidden lg:block mt-8 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{product.description}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Similar Products */}
        <div className="mt-16 border-t border-gray-200 pt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Similar Products</h2>
            <div className="flex gap-2">
              <button className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {similarProducts.map((item) => (
              <div key={item.id} className="group cursor-pointer bg-gray-50 rounded-xl p-4 hover:shadow-lg transition-shadow">
                <div className={`aspect-square rounded-lg ${item.image} mb-3`} />
                <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">{item.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900">${item.price}</span>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-gray-600">{item.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Design 3: Minimal Single Column
  const Design3 = () => (
    <div className="min-h-screen bg-white" style={containerStyles}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Images */}
        <div className="mb-8">
          <div className={`aspect-[3/4] md:aspect-[16/10] rounded-2xl ${product.images[selectedImage]} mb-4 relative`}>
            {product.originalPrice && (
              <div className="absolute top-4 left-4 bg-gray-900 text-white px-4 py-2 rounded-full font-semibold text-sm">
                {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
              </div>
            )}
          </div>
          
          <div className="flex gap-3 overflow-x-auto pb-2">
            {product.images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`w-20 h-20 md:w-24 md:h-24 rounded-lg ${img} flex-shrink-0 ${selectedImage === idx ? 'ring-2 ring-gray-900' : 'opacity-50'}`}
              />
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
                />
              ))}
            </div>
            <span className="text-gray-600">{product.rating}</span>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <span className="text-4xl font-bold text-gray-900">${product.price}</span>
            {product.originalPrice && (
              <>
                <span className="text-xl text-gray-500 line-through">${product.originalPrice}</span>
                <span className="text-green-600 font-semibold">
                  Save ${(product.originalPrice - product.price).toFixed(2)}
                </span>
              </>
            )}
          </div>

          <p className="text-gray-700 leading-relaxed mb-8">{product.description}</p>

          {/* Options */}
          <div className="space-y-6 mb-8">
            {/* Color */}
            <div>
              <label className="font-semibold text-gray-900 mb-3 block">
                Select Color: <span className="font-normal text-gray-600">{selectedColor}</span>
              </label>
              <div className="flex gap-3">
                {product.colors.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(color.name)}
                    className={`w-14 h-14 rounded-lg ${color.class} ${selectedColor === color.name ? 'ring-2 ring-offset-2 ring-gray-900' : ''}`}
                  />
                ))}
              </div>
            </div>

            {/* Size */}
            <div>
              <label className="font-semibold text-gray-900 mb-3 block">
                Select Size: <span className="font-normal text-gray-600">{selectedSize}</span>
              </label>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`w-16 h-12 border-2 rounded-lg font-medium transition-colors ${selectedSize === size ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 hover:border-gray-900'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div>
              <label className="font-semibold text-gray-900 mb-3 block">Quantity</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border-2 border-gray-300 rounded-lg">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-14 h-14 flex items-center justify-center hover:bg-gray-50">
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="flex-1 text-center font-bold text-lg">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-14 h-14 flex items-center justify-center hover:bg-gray-50">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <span className="text-gray-600">
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 mb-8">
            <button className="w-full bg-gray-900 text-white py-5 rounded-xl font-semibold text-lg hover:bg-gray-800 transition-colors">
              Add to Cart
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setIsWishlisted(!isWishlisted)} className="border-2 border-gray-300 py-4 rounded-xl font-medium hover:border-gray-900 transition-colors flex items-center justify-center gap-2">
                <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
                Wishlist
              </button>
              <button className="border-2 border-gray-300 py-4 rounded-xl font-medium hover:border-gray-900 transition-colors flex items-center justify-center gap-2">
                <Share2 className="w-5 h-5" />
                Share
              </button>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center gap-3 text-sm">
              <Truck className="w-6 h-6 text-gray-700" />
              <span className="text-gray-700">Free shipping on orders over $50</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <RotateCcw className="w-6 h-6 text-gray-700" />
              <span className="text-gray-700">30-day easy returns</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Shield className="w-6 h-6 text-gray-700" />
              <span className="text-gray-700">Secure checkout guaranteed</span>
            </div>
          </div>

          {/* Features */}
          <div className="border-t border-gray-200 pt-8">
            <h3 className="font-bold text-gray-900 mb-4 text-lg">Product Features</h3>
            <ul className="space-y-3">
              {product.features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-3 text-gray-700">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Similar Products */}
        <div className="mt-16 border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Complete Your Look</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {similarProducts.map((item) => (
              <div key={item.id} className="group cursor-pointer">
                <div className={`aspect-square rounded-xl ${item.image} mb-3 group-hover:scale-105 transition-transform`} />
                <h3 className="font-medium text-gray-900 mb-2 line-clamp-2 text-sm">{item.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900">${item.price}</span>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm text-gray-600">{item.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDesign = () => {
    switch (activeDesign) {
      case 'design1':
        return <Design1 />;
      case 'design2':
        return <Design2 />;
      case 'design3':
        return <Design3 />;
      default:
        return <Design1 />;
    }
  };

  return renderDesign();
};

export default ProductView;